import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMediaQuery, useTheme } from "@mui/material";
import FooterManual from "./footerManual";
import Notes from "./header";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";
const EXTRA_LIGHT_RED_HOVER = "#FFF5F5";

// Styled Components
const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

// FAQ Accordion Component
const FAQAccordion = ({ question, answer, isOpen, onClick, index }) => (
    <Box
        sx={{
            border: `1px solid ${isOpen ? RED : '#E5E7EB'}`,
            borderRadius: "8px",
            marginBottom: "12px",
            overflow: "hidden",
            transition: "all 0.3s ease",
            backgroundColor: isOpen ? LIGHT_RED_BG : "#fff",
            boxShadow: isOpen ? `0 4px 12px rgba(220, 38, 38, 0.1)` : "none",
        }}
    >
        <Box
            onClick={onClick}
            sx={{
                padding: "16px 20px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: isOpen ? RED : "transparent",
                color: isOpen ? "white" : BLACK_TEXT,
                transition: "all 0.3s ease",
                '&:hover': {
                    backgroundColor: isOpen ? RED : LIGHT_RED_BG,
                }
            }}
        >
            <Typography sx={{ fontWeight: "600", fontSize: "15px", display: 'flex', alignItems: 'center' }}>
                <span style={{
                    backgroundColor: isOpen ? "white" : RED,
                    color: isOpen ? RED : "white",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                    marginRight: "12px"
                }}>
                    {index + 1}
                </span>
                {question}
            </Typography>
            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: isOpen ? "white" : RED }}></i>
        </Box>
        {isOpen && (
            <Box sx={{ padding: "20px", borderTop: `1px solid ${RED}` }}>
                {typeof answer === 'string' ? (
                    <Typography sx={{ color: GRAY_TEXT, fontSize: "14px", lineHeight: "1.6" }}>
                        {answer}
                    </Typography>
                ) : (
                    answer
                )}
            </Box>
        )}
    </Box>
);

// FAQ Category Section
const FAQCategory = ({ title, icon, faqs, openIndex, setOpenIndex, startIndex }) => (
    <Box
        sx={{
            border: `2px solid ${DARK_RED_ACCENT}`,
            borderRadius: "12px",
            overflow: "hidden",
            marginBottom: "30px",
            boxShadow: `0 4px 12px rgba(220, 38, 38, 0.1)`,
            transition: "transform 0.3s ease",
            '&:hover': {
                transform: "translateY(-5px)",
                boxShadow: `0 8px 20px rgba(220, 38, 38, 0.15)`,
            }
        }}
    >
        <Box
            sx={{
                backgroundColor: RED,
                color: "white",
                padding: "18px 24px",
                borderBottom: `2px solid ${DARK_RED_ACCENT}`
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center' }}>
                <i className={`fas fa-${icon}`} style={{ marginRight: '12px' }}></i>
                {title}
            </Typography>
        </Box>
        <Box sx={{ padding: "20px", backgroundColor: "#fff" }}>
            {faqs.map((faq, index) => (
                <FAQAccordion
                    key={index}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openIndex === startIndex + index}
                    onClick={() => setOpenIndex(openIndex === startIndex + index ? null : startIndex + index)}
                    index={index}
                />
            ))}
        </Box>
    </Box>
);

const Faqs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    const [openIndex, setOpenIndex] = useState(0);

    // FAQ Data organized by categories
    const faqCategories = [
        {
            title: "About Amar Enterprises Co Ltd",
            icon: "building",
            faqs: [
                {
                    question: "What is Amar Enterprises Co Ltd?",
                    answer: "Amar Enterprises Co Ltd is a licensed Japanese company with a 17-year legacy specializing in the sale and export of Japanese Used Cars, New Cars, Auto Parts, Tires, Wheels, and Scrap. We operate out of Saitama-Shi, Japan."
                },
                {
                    question: "Are you a licensed and reliable exporter?",
                    answer: "Yes. We operate under strict Japanese commercial law and hold a valid license: Saitama Prefectural Public Safety Commission No. 431260012433. Qualification Registration Number: T7-0300-0202-3122, Our long operational history reinforces our reliability in the global market."
                },
                {
                    question: "What are your office hours?",
                    answer: "Our office hours are Monday to Saturday, 9:00 to 17:00 JST (Japan Standard Time). We are closed on Sundays, public holidays, summer holidays, year-end, and New Year holidays."
                },
                {
                    question: "Where is your office located?",
                    answer: "Our main operational hub and yard are located at: 1076-1 Ooaza Komizou Iwatsuki Saitama-Shi Saitama-Ken - 339-0003, Japan."
                }
            ]
        },
        {
            title: "Account Information & Support",
            icon: "user-headset",
            faqs: [
                {
                    question: "How do I contact the sales team?",
                    answer: "You can reach our sales and customer support team via: Mobile: +81-48-793-3911 or Email: sales@amar.co.jp. We offer dedicated support to assist with your purchasing journey."
                },
                {
                    question: "Do I need to create an account to view inventory?",
                    answer: "You can view our public inventory without an account. However, creating an account is necessary to submit quote requests, track your order, and log in to your account on the website."
                },
                {
                    question: "Do you offer after-sale service?",
                    answer: 'While our vehicle sales are "AS-IS," our commitment includes comprehensive after-sale documentation service, ensuring you receive all necessary original shipping documents (B/L, Export Certificate) for smooth customs clearance.'
                }
            ]
        },
        {
            title: "Vehicle Condition and Inventory",
            icon: "car",
            faqs: [
                {
                    question: "Are the vehicles you sell new or used?",
                    answer: "We sell both New Cars and Used Cars. Our primary business and expertise are in exporting high-quality, pre-owned Japanese vehicles."
                },
                {
                    question: 'What is a "Modified Car"?',
                    answer: "A Modified Car is a vehicle that has been customized with performance, aesthetic, or functional upgrades. We specialize in sourcing and exporting these unique vehicles, including specific JDM performance models."
                },
                {
                    question: "Can you guarantee the mileage on a used vehicle?",
                    answer: "We guarantee the mileage as recorded on the Japan Export Certificate (Deregistration Certificate) at the time the car was exported from Japan. Odometer tampering is illegal in Japan, and we rely on official documentation to verify mileage."
                },
                {
                    question: "Are the vehicles left-hand drive (LHD)?",
                    answer: "The vast majority of vehicles sourced in Japan (JDM models) are Right-Hand Drive (RHD). If an LHD vehicle is available, it will be explicitly marked in the listing details. We do not perform steering conversions."
                },
                {
                    question: "Can I request a specific model or car part not listed?",
                    answer: "Yes, absolutely! We offer Customized Solutions. Please contact our sales team with the exact make, model, year, and specifications, and we will utilize our network to source the item for you."
                }
            ]
        },
        {
            title: "Reservation and Purchase",
            icon: "shopping-cart",
            faqs: [
                {
                    question: "How do I reserve a vehicle?",
                    answer: "Vehicles are reserved once a Pro-forma Invoice is issued and a required deposit (if applicable) or full payment is received. Due to demand, vehicles are sold on a first-come, first-served basis upon payment confirmation."
                },
                {
                    question: "What is the process for buying an item from Amar Car Website?",
                    answer: (
                        <Box sx={{ color: GRAY_TEXT, fontSize: "14px", lineHeight: "1.6" }}>
                            <Typography sx={{ color: BLACK_TEXT, fontWeight: "600", marginBottom: "15px" }}>
                                Follow these steps to complete your purchase:
                            </Typography>

                            <Box sx={{
                                counterReset: 'step-counter',
                                '& > div': {
                                    position: 'relative',
                                    paddingLeft: '50px',
                                    marginBottom: '20px',
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                },
                                '& > div:before': {
                                    counterIncrement: 'step-counter',
                                    content: 'counter(step-counter)',
                                    position: 'absolute',
                                    left: '0',
                                    top: '0',
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: RED,
                                    color: 'white',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }
                            }}>
                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Find Your Product
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Browse and select a vehicle from our website:{" "}
                                        <Box
                                            component="a"
                                            href="https://car.amar.co.jp/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                color: RED,
                                                textDecoration: 'none',
                                                fontWeight: '500',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            https://car.amar.co.jp/
                                        </Box>
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Send Inquiry & Receive Invoice
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Contact us with your Consignee and Notify Party information (Full name, address, Tel number)
                                        to receive a detailed invoice including payment details, purchase conditions, bank account
                                        information, deadlines, and payment methods.
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Initiate Payment & Share Receipt
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Make payment to your preferred bank as per the invoice and share the T/T copy within
                                        <Box component="span" sx={{ color: RED, fontWeight: "600" }}> 48 hours</Box> to secure your purchase.
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Complete Your Payment
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Finalize payment to our bank account and email the receipt. Payment confirmation is
                                        typically completed within <Box component="span" sx={{ color: RED, fontWeight: "600" }}>24 hours</Box>.
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Receive Confirmation & Track Shipment
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Once payment is confirmed, we begin the shipping process. You'll receive shipping schedules
                                        and updates. Track your vehicle through your account (Order History → Track Shipment).
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Receive Shipping Documents
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Receive the Bill of Lading (BL) and other standard documents (Export Certificate, etc.)
                                        after the product is shipped.
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography sx={{ fontWeight: "600", color: BLACK_TEXT, marginBottom: "5px" }}>
                                        Vehicle Arrival & Delivery
                                    </Typography>
                                    <Typography sx={{ marginLeft: "5px" }}>
                                        Your vehicle arrives at the destination port. Clear customs with the provided documents.
                                        Optional City Delivery Service is available. Enjoy your new car!
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    backgroundColor: LIGHT_RED_BG,
                                    padding: "15px",
                                    borderRadius: "8px",
                                    border: `1px solid ${RED}`,
                                    marginTop: "20px"
                                }}
                            >
                                <Typography sx={{ color: BLACK_TEXT, fontWeight: "600", marginBottom: "5px" }}>
                                    Need More Details?
                                </Typography>
                                <Typography>
                                    Visit our{" "}
                                    <Box
                                        component="a"
                                        href="https://car.amar.co.jp/purchaseFlow"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: RED,
                                            textDecoration: 'none',
                                            fontWeight: '500',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Purchase Flow section
                                    </Box>{" "}
                                    for a comprehensive explanation of the buying process.
                                </Typography>
                            </Box>
                        </Box>
                    )
                },
                {
                    question: 'Are your vehicles sold "AS-IS"?',
                    answer: 'Yes. All New & Used Cars, Parts, and Scraps are sold on an "AS-IS, WHERE-IS" basis unless a specific, written warranty is provided on the Pro-forma Invoice. This is standard practice in international used goods export.'
                }
            ]
        },
        {
            title: "Payment and Refund",
            icon: "credit-card",
            faqs: [
                {
                    question: "What payment methods do you accept?",
                    answer: "We accept International Bank Transfers (T/T / NEFT / SWIFT) which are mandatory for large purchases. We also accept payments via secure Payment Gateways (Credit/Debit Card, Netbanking) and PayPal (for small amounts), subject to processing fees."
                },
                {
                    question: "In which currency should I pay?",
                    answer: "We primarily accept payment in Japanese Yen (JPY) or US Dollars (USD). The required currency will be clearly stated on your Pro-forma Invoice."
                },
                {
                    question: "Who pays the bank charges for T/T transfers?",
                    answer: 'The Customer is responsible for all bank charges (sending, intermediary, and receiving). You must select the "OUR" option when initiating the transfer to ensure the full invoice amount arrives in our account.'
                },
                {
                    question: "What is your refund/return policy?",
                    answer: 'Our policy is strictly "No Claim, No Return." Sales are final. Refunds are only considered in the extremely limited case of our verifiable error (e.g., shipping the wrong item), or if the goods are lost before the risk transfers to you (i.e., before loading).'
                },
                {
                    question: "Can I pay cash at your office?",
                    answer: "Yes. We accept payment at our office for purchased items, but this must be arranged and scheduled in advance with our sales team."
                }
            ]
        },
        {
            title: "Shipping and Delivery",
            icon: "shipping-fast",
            faqs: [
                {
                    question: "What shipping methods do you use?",
                    answer: "We specialize in international shipping via container and RORO (Roll-On/Roll-Off) cargo logistics, reaching destinations across Africa, Oceania, Asia, Europe, and the Americas. We select the fastest, most cost-effective method for your cargo."
                },
                {
                    question: "What is the difference between FOB, CFR, and CIF?",
                    answer: 'These are Incoterms that define cost and risk: FOB: Price includes cost to load onto the ship in Japan; Buyer pays freight and insurance. CFR: Price includes cost and freight to your port; Buyer pays insurance. CIF: Price includes cost, freight, and insurance to your port. In all three cases, the risk of damage transfers to the Buyer once the goods are loaded onto the vessel in Japan.'
                },
                {
                    question: "How long does shipping take?",
                    answer: "Shipping to the port of discharge typically takes 2 to 6 weeks, depending on the destination and vessel availability. This time is estimated and subject to change by the carrier."
                },
                {
                    question: "Who is responsible for import duties and customs?",
                    answer: "The Buyer is 100% responsible for all import duties, tariffs, taxes (VAT/GST), and customs clearance procedures in the destination country. Amar Enterprises Co Ltd is not liable for import compliance failure."
                },
                {
                    question: "When do I receive the original shipping documents?",
                    answer: "Original documents (Bill of Lading, Export Certificate, etc.) are sent via First-Class Air Courier only after the full, cleared payment has been received in our bank account."
                }
            ]
        },
        {
            title: "Policy Questions and Legal Compliance",
            icon: "balance-scale",
            faqs: [
                {
                    question: "Where can I read your full legal policies?",
                    answer: "The full details of our obligations and your responsibilities are contained within our Terms and Conditions, Shipping Policy, and Privacy Policy pages, which are governed by the laws of the Japan Jurisdiction only."
                },
                {
                    question: "What is your commitment to quality?",
                    answer: 'We maintain the Highest Quality Standards by using a "Systems Driven Process" for vehicle checks, sourcing premium parts, and providing swift processing for documents to ensure efficiency and reduce errors.'
                }
            ]
        }
    ];

    // Calculate starting indices for each category
    let currentIndex = 0;
    const categoryStartIndices = faqCategories.map(category => {
        const startIndex = currentIndex;
        currentIndex += category.faqs.length;
        return startIndex;
    });

    return (
        <Box
            className="container-main-AllSeeModels"
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#fff"
            }}
        >
            {/* Header/Notes Component */}
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Banner Section */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
                    backgroundColor: "black",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src="/image/AllSeeModelsBackgroungCar.png" 
                    alt="Automotive Background"
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.35,
                    }}
                />

                <Typography
                    sx={{
                        position: "relative",
                        color: "white",
                        fontWeight: "900",
                        textTransform: "uppercase",
                        letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
                        fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
                        zIndex: 2,
                        textShadow: "0 0 10px rgba(0,0,0,0.8)",
                        textAlign: "center",
                        lineHeight: 1.1,
                        px: "20px",
                    }}
                >
                    Frequently Asked Questions
                </Typography>
            </Box>

            {/* Main Content Container */}
            <Box 
                sx={{
                    width: isMobile ? "95%" : isTablet ? "92%" : "85%",
                    backgroundColor: "#FFFFFF",
                    margin: "0 auto",
                    marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
                    borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
                    padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
                    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
                    zIndex: 10,
                    position: "relative",
                }}
            >
                {/* Introduction Section */}
                <Box sx={{ textAlign: "center", marginBottom: isMobile ? "35px" : isTablet ? "45px" : "60px" }}>
                    <Typography
                        sx={{
                            fontWeight: "800",
                            color: DARK_RED_ACCENT,
                            marginBottom: isMobile ? "10px" : "12px",
                            textTransform: "uppercase",
                            fontSize: isMobile ? "18px" : isTablet ? "24px" : "32px",
                            lineHeight: 1.3,
                        }}
                    >
                        Welcome to Our FAQ Section
                    </Typography>
                    <Typography
                        sx={{
                            color: GRAY_TEXT,
                            fontSize: isMobile ? "13px" : isTablet ? "14px" : "16px",
                            maxWidth: "900px",
                            margin: "0 auto",
                            lineHeight: "1.7",
                            px: isMobile ? "12px" : "0",
                        }}
                    >
                        We provide transparent answers to common questions regarding the purchase and export of <b>New & Used Japanese Cars</b>, <b>Auto Parts</b>, and our comprehensive services. Find everything you need to know about working with Amar Enterprises Co Ltd.
                    </Typography>
                </Box>

                {/* Quick Stats Bar */}
                <Box 
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                        gap: isMobile ? "12px" : isTablet ? "15px" : "20px",
                        marginBottom: isMobile ? "30px" : isTablet ? "40px" : "50px",
                    }}
                >
                    {[
                        { icon: "calendar-alt", value: "17+", label: "Years Experience" },
                        { icon: "car", value: "1000+", label: "Vehicles Exported" },
                        { icon: "globe-americas", value: "50+", label: "Countries Served" },
                        { icon: "star", value: "99%", label: "Customer Satisfaction" }
                    ].map((stat, index) => (
                        <Box 
                            key={index}
                            sx={{
                                backgroundColor: LIGHT_RED_BG,
                                padding: isMobile ? "12px" : isTablet ? "15px" : "20px",
                                borderRadius: "8px",
                                textAlign: "center",
                                border: `1px solid ${RED}`,
                                transition: "all 0.3s ease",
                                '&:hover': {
                                    transform: "translateY(-3px)",
                                    boxShadow: `0 5px 15px rgba(220, 38, 38, 0.2)`,
                                }
                            }}
                        >
                            <i className={`fas fa-${stat.icon}`} style={{ color: RED, fontSize: isMobile ? "20px" : "24px", marginBottom: "8px" }}></i>
                            <Typography sx={{ fontWeight: "bold", color: RED, fontSize: isMobile ? "16px" : "20px" }}>{stat.value}</Typography>
                            <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "11px" : "12px" }}>{stat.label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* FAQ Categories */}
                {faqCategories.map((category, categoryIndex) => (
                    <FAQCategory
                        key={categoryIndex}
                        title={category.title}
                        icon={category.icon}
                        faqs={category.faqs}
                        openIndex={openIndex}
                        setOpenIndex={setOpenIndex}
                        startIndex={categoryStartIndices[categoryIndex]}
                    />
                ))}

                {/* Contact CTA Section */}
                <Box 
                    sx={{
                        backgroundColor: LIGHT_RED_BG,
                        border: `2px solid ${RED}`,
                        borderRadius: isMobile ? "12px" : "16px",
                        padding: isMobile ? "20px 16px" : isTablet ? "28px 24px" : "40px",
                        textAlign: "center",
                        marginTop: isMobile ? "30px" : isTablet ? "40px" : "50px"
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: RED, marginBottom: "15px", fontSize: isMobile ? "18px" : "24px" }}>
                        Still Have Questions?
                    </Typography>
                    <Typography sx={{ color: GRAY_TEXT, marginBottom: "20px", fontSize: isMobile ? "13px" : isTablet ? "14px" : "16px" }}>
                        Our dedicated support team is here to help you with any additional questions.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: isMobile ? "10px" : "20px", flexWrap: "wrap", flexDirection: { xs: "column", sm: "row" } }}>
                        <Box 
                            sx={{ 
                                display: "flex", 
                                alignItems: "center", 
                                backgroundColor: "white", 
                                padding: isMobile ? "10px 16px" : "12px 20px", 
                                borderRadius: "8px",
                                border: `1px solid ${RED}`,
                                justifyContent: { xs: "center", sm: "flex-start" }
                            }}
                        >
                            <i className="fas fa-phone" style={{ color: RED, marginRight: "10px", fontSize: isMobile ? "14px" : "16px" }}></i>
                            <Typography sx={{ color: BLACK_TEXT, fontWeight: "500", fontSize: isMobile ? "13px" : "14px" }}>+81-48-793-3911</Typography>
                        </Box>
                        <Box 
                            sx={{ 
                                display: "flex", 
                                alignItems: "center", 
                                backgroundColor: "white", 
                                padding: isMobile ? "10px 16px" : "12px 20px", 
                                borderRadius: "8px",
                                border: `1px solid ${RED}`,
                                justifyContent: { xs: "center", sm: "flex-start" }
                            }}
                        >
                            <i className="fas fa-envelope" style={{ color: RED, marginRight: "10px", fontSize: isMobile ? "14px" : "16px" }}></i>
                            <Typography sx={{ color: BLACK_TEXT, fontWeight: "500", fontSize: isMobile ? "13px" : "14px" }}>sales@amar.co.jp</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Space Filler for Footer Alignment */}
            <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />

            {/* Footer Component */}
            <FooterManual />
        </Box>
    );
};

export default Faqs;